import { Component, Type, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { RailLayoutService } from './rail-layout.service';

/**
 * Menu-axis Phase 2 — RailHostComponent
 *
 * Generic left-rail host, sized by --rail-w (src/styles.scss). Reads the active route's `data.rail`
 * (a component class) and renders it via `*ngComponentOutlet`. When no
 * rail is declared on the route, renders nothing — the flex layout
 * collapses the column naturally.
 *
 * Routes declare a rail via: `data: { rail: MyRailComponent }`
 * Routes that should preserve the previous rail use: `data: { preserveRail: true }`
 */
@Component({
  selector: 'app-rail-host',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (railComponent()) {
      <aside class="rail" [class.is-collapsed]="railLayout.collapsed()">
        <button type="button" class="rail-toggle"
                [attr.aria-expanded]="!railLayout.collapsed()"
                [attr.aria-label]="railLayout.collapsed() ? 'Show filters' : 'Hide filters'"
                [title]="railLayout.collapsed() ? 'Show filters' : 'Hide filters'"
                (click)="railLayout.toggle()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path [attr.d]="railLayout.collapsed() ? 'M9 6l6 6-6 6' : 'M15 6l-6 6 6 6'" />
          </svg>
          @if (railLayout.collapsed()) {
            <span class="rt-label">FILTERS</span>
          }
        </button>
        <!-- The rail component stays MOUNTED when collapsed and is hidden in
             CSS. Unmounting it would destroy its local UI state - which
             accordion is open, which picker is expanded - so hiding and
             showing the filters would silently reset them. -->
        <ng-container *ngComponentOutlet="railComponent()!" />
      </aside>
    }
  `,
  styleUrl: './rail-host.component.scss',
})
export class RailHostComponent {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  /** Public: the template binds the toggle, and App binds the collapsed class
   *  on .rail-col (the tray lives there, outside this component). */
  protected readonly railLayout = inject(RailLayoutService);

  private _lastRail: Type<unknown> | null = null;

  protected readonly railComponent = toSignal<Type<unknown> | null>(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.resolveRail()),
      startWith(this.resolveRail()),
    ),
    { initialValue: null },
  );

  /** Public so the shell can react to "no rail on this route" — e.g. to stop
   *  pinning the selection tray to the bottom of an otherwise-empty column. */
  readonly hasRail = computed(() => this.railComponent() !== null);

  private resolveRail(): Type<unknown> | null {
    let r = this.route;
    while (r.firstChild) r = r.firstChild;
    const data = r.snapshot.data ?? {};
    const rail = (data['rail'] as Type<unknown> | undefined) ?? null;
    if (rail) {
      this._lastRail = rail;
      return rail;
    }
    if (data['preserveRail'] === true) {
      return this._lastRail;
    }
    return null;
  }
}
